import { Row, Space } from "antd";
import { SelectAllButton } from "components/FixtureView/components/SelectAllButton";
import { SelectNoneButton } from "components/FixtureView/components/SelectNoneButton";
import { EditModeToggle } from "components/Shared/EditModeToggle";
import { RosterAndStaffTable } from "components/Shared/RosterAndStaffTable";
import { SearchInput } from "components/Shared/SearchInput";
import { PeopleTypes } from "enums/peopleTypes";
import { useAddFixtureStaffPersons, useDeleteFixtureStaffPersons } from "hooks/useFixtureStaffApi";
import { useUpdateFixtureStaffPerson } from "hooks/useUpdateStaffPerson";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { filterPersonList } from "utils/filterPersonList";
import { getPersonDeleteApiData } from "utils/getPersonDeleteApiData";
import { runAsyncAction } from "utils/runAsyncAction";
import { useRecordsState } from "../../../hooks/useRecordsState";
import { useFixtureStaffColumns } from "../hooks/useFixtureStaffColumns";
import { CopyPeople } from "./CopyPeople";

type Props = {
  isLoading: boolean;
  fixture: any;
  staff: Array<any>;
  viewOnly: boolean;
};

export const FixtureStaffTab = ({ isLoading, fixture, staff, viewOnly }: Props) => {
  const { entityId: entityIdFromParams, seasonId: seasonIdFromParams, fixtureId: fixtureIdFromParams } = useParams();
  const entityId = entityIdFromParams || "undefined";
  const seasonId = seasonIdFromParams || "undefined";
  const fixtureId = fixtureIdFromParams || "undefined";
  const [searchText, setSearchText] = useState("");
  const [editMode, setEditMode] = useState(false);
  const filteredStaff = filterPersonList(staff, searchText);
  const { isPending: areRecordsAdding, mutateAsync: addRecordsApi } = useAddFixtureStaffPersons();
  const { isPending: areRecordsDeleting, mutateAsync: deleteRecordsApi } = useDeleteFixtureStaffPersons();
  const { updatingRecord, isUpdating: isRecordBeingUpdated, mutateAsync: updateRecord } = useUpdateFixtureStaffPerson();

  const { checkIfRecordBeingBusy, ensureRecordsBusy } = useRecordsState({
    isAddingOrDeleting: areRecordsDeleting || areRecordsAdding,
    isUpdating: isRecordBeingUpdated,
    updatingRecord,
    applyForAll: !fixture?.taskStatus,
  });

  const areUserControlsDisabled =
    isLoading || areRecordsDeleting || areRecordsAdding || !fixture?.areRosterAndStaffEditable;

  const addRecords = (records: Array<any>) => {
    if ((records?.length || 0) === 0) {
      return;
    }
    runAsyncAction(
      ensureRecordsBusy(records, async () =>
        addRecordsApi(
          {
            entityId,
            seasonId,
            fixtureId,
            data: {
              persons: records.map((x: any) => ({
                dcPersonId: x?.dcPersonId,
                personId: x?.personId,
                role: x?.role,
              })),
            },
          },
          {
            onSuccess: () =>
              records.forEach((x: any) => {
                x.isActive = true;
              }),
          }
        )
      ),
      "Failed to add fixture staff records"
    );
  };

  const deleteRecords = (records: Array<any>) => {
    if ((records?.length || 0) === 0) {
      return;
    }
    runAsyncAction(
      ensureRecordsBusy(records, async () =>
        deleteRecordsApi(
          {
            entityId,
            seasonId,
            fixtureId,
            data: getPersonDeleteApiData(records),
          },
          {
            onSuccess: () =>
              records.forEach((x: any) => {
                x.isActive = false;
              }),
          }
        )
      ),
      "Failed to delete fixture staff records"
    );
  };

  const onSelectAllClicked = () => addRecords(staff.filter((x: any) => !x.isActive));

  const onSelectNoneClicked = () => deleteRecords(staff.filter((x: any) => x.isActive));

  const columns = useFixtureStaffColumns({
    viewOnly: viewOnly || !fixture?.areRosterAndStaffEditable,
    editMode,
    checkIfRecordBeingBusy,
    updateRecord: (data: any, record: any) => {
      runAsyncAction(
        updateRecord({
          entityId,
          seasonId,
          fixtureId,
          personId: record?.personId,
          dcPersonId: record?.dcPersonId,
          data,
          record,
        }),
        "Failed to update fixture staff record"
      );
    },
    addRecord: (record: any) => addRecords([record]),
    deleteRecord: (record: any) => deleteRecords([record]),
  });

  return (
    <>
      <Row className="mb-10" justify="end" align="middle">
        <Space>
          {!viewOnly && (
            <>
              <EditModeToggle disabled={areUserControlsDisabled} onChange={setEditMode} />
              <SelectAllButton
                disabled={areUserControlsDisabled}
                isLoading={areRecordsAdding}
                onClick={onSelectAllClicked}
              />
              <SelectNoneButton
                disabled={areUserControlsDisabled}
                isLoading={areRecordsDeleting}
                onClick={onSelectNoneClicked}
              />
              <CopyPeople
                type={PeopleTypes.Staff}
                disabled={areUserControlsDisabled}
                entityId={entityId}
                seasonId={seasonId}
                currentFixtureId={fixtureId}
              />
            </>
          )}
          <SearchInput onSearch={setSearchText} />
        </Space>
      </Row>
      <Row>
        <RosterAndStaffTable isLoading={isLoading} records={filteredStaff} columns={columns} />
      </Row>
    </>
  );
};
